
import React from "react";
import { Link } from "react-router-dom";
import { FileText } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { OrganizationWithRelations } from "@/types/organization.types";
import { OpportunityBasic } from "@/types/opportunity.types";

interface OpportunitiesTabProps {
  organization: OrganizationWithRelations;
  id: string;
}

export function OpportunitiesTab({ organization, id }: OpportunitiesTabProps) {
  const opportunityCount = organization.opportunities?.length || 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Opportunities</CardTitle>
          <CardDescription>
            Sales opportunities with {organization.name}
          </CardDescription>
        </div>
        <Button asChild size="sm">
          <Link to={`/opportunities/create?organization=${id}`}>
            <FileText className="mr-2 h-4 w-4" />
            Add Opportunity
          </Link>
        </Button>
      </CardHeader>
      <CardContent>
        {!organization.opportunities || organization.opportunities.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="mx-auto h-12 w-12 text-muted-foreground opacity-50" />
            <h3 className="mt-2 text-lg font-medium">No opportunities yet</h3>
            <p className="mt-1 text-muted-foreground">
              Get started by adding your first opportunity for this organization
            </p>
            <Button asChild className="mt-4">
              <Link to={`/opportunities/create?organization=${id}`}>
                Add Opportunity
              </Link>
            </Button>
          </div>
        ) : (
          <div className="divide-y">
            {organization.opportunities.map((opportunity: OpportunityBasic) => (
              <div key={opportunity.id} className="py-3 flex justify-between items-center">
                <div>
                  <Link 
                    to={`/opportunities/${opportunity.id}`}
                    className="font-medium hover:underline"
                  >
                    {opportunity.name}
                  </Link>
                  <div className="text-sm text-muted-foreground">
                    {opportunity.stage}
                    {opportunity.value && ` • $${opportunity.value.toLocaleString()}`}
                    {opportunity.probability !== undefined && ` • ${opportunity.probability}%`}
                  </div>
                </div>
                <Button asChild variant="ghost" size="sm">
                  <Link to={`/opportunities/${opportunity.id}`}>
                    View
                  </Link>
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
